import { Injectable } from '@angular/core';

import { FootballScore, IcehockeyPeriodGameScore, PeriodGameScore, ScoreboardType, SetGameScore } from '@frontend/sports/betting-offer/feature/model';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { sumBy } from 'lodash-es';

import { DartsScoreboardViewModel } from '../../scoreboards/models/darts-scoreboard.viewmodel';
import { PairScoreboardViewModel } from '../../scoreboards/models/pair-scoreboard.viewmodel';
import { PenaltyShootoutScoreboardViewModel } from '../../scoreboards/models/penalty-shootout-scoreboard.viewmodel';
import { ScoreboardViewModel } from '../../scoreboards/models/scoreboard.viewmodel';
import { SetScoreboardViewModel, TennisScoreboardViewModel } from '../../scoreboards/models/set-scoreboard.viewmodel';
import { SnookerScoreboardViewModel } from '../../scoreboards/models/snooker-scoreboard.viewmodel';
import { SoccerScoreboardViewModel } from '../../scoreboards/models/soccer-scoreboard.viewmodel';
import { MyBetsEventModel } from '../models/my-bets-event.model';

@Injectable()
export class MyBetsCondensedScoreboardService {
    getTotalsScore(event: MyBetsEventModel, baseScoreboardViewModel: ScoreboardViewModel): string {
        if (!this.isEventEligibleForScore(event) || this.isExcludedScoreboardType(event)) {
            return '';
        }

        switch (event.sport.id) {
            case SportConstant.Soccer:
                return this.getSoccerScore(event, baseScoreboardViewModel);
            case SportConstant.Darts:
                return this.getDartsScore(baseScoreboardViewModel);
            case SportConstant.Tennis:
                return this.getTennisScore(baseScoreboardViewModel);
            case SportConstant.AmericanFootball:
            case SportConstant.Icehockey:
            case SportConstant.Basketball:
                return this.getPeriodGameScore(event, baseScoreboardViewModel);
            case SportConstant.Snooker:
                return this.getSnookerScore(baseScoreboardViewModel);
            case SportConstant.Baseball:
                return this.getBaseballScore(event);
            default:
                return this.getDefaultScore(event, baseScoreboardViewModel);
        }
    }

    private isEventEligibleForScore(event: MyBetsEventModel): boolean {
        return event.live && event.scoreboard.started && event.participants.length > 0;
    }

    private isExcludedScoreboardType(event: MyBetsEventModel): boolean {
        return event.scoreboard.type === ScoreboardType.TournamentGame || event.scoreboard.type === ScoreboardType.LeaderboardGame;
    }

    private getSoccerScore(event: MyBetsEventModel, baseScoreboardViewModel: ScoreboardViewModel): string {
        const score = event.scoreboard.getScore<FootballScore>();
        if (score?.penaltyShootOutScore) {
            const scoreboardModel = baseScoreboardViewModel as PenaltyShootoutScoreboardViewModel;

            return this.getScore(scoreboardModel.stats.penaltyScore[0], scoreboardModel.stats.penaltyScore[1]);
        }

        const scoreboardModel = baseScoreboardViewModel as SoccerScoreboardViewModel;
        if (!scoreboardModel.stats.list.length) {
            return this.getScore(0, 0);
        }

        const stats = scoreboardModel.stats.list.find((item) => item.isVisible && item.isEnabled);

        return this.getScore(stats?.player1 || 0, stats?.player2 || 0);
    }

    private getDartsScore(baseScoreboardViewModel: ScoreboardViewModel): string {
        const dartsModel = baseScoreboardViewModel as DartsScoreboardViewModel;

        return this.getScore(dartsModel.stats.legs.participant1, dartsModel.stats.legs.participant2);
    }

    private getTennisScore(baseScoreboardViewModel: ScoreboardViewModel): string {
        const tennisModel = baseScoreboardViewModel as TennisScoreboardViewModel;

        return this.getScore(tennisModel.stats.points?.player1 || 0, tennisModel.stats.points?.player2 || 0);
    }

    private getPeriodGameScore(event: MyBetsEventModel, baseScoreboardViewModel: ScoreboardViewModel): string {
        const score = event.scoreboard.getScore<IcehockeyPeriodGameScore>();
        if (score?.penaltyShootOutScore) {
            const scoreboardModel = baseScoreboardViewModel as PenaltyShootoutScoreboardViewModel;

            return this.getScore(scoreboardModel.stats.penaltyScore[0], scoreboardModel.stats.penaltyScore[1]);
        }

        const periodScore = event.scoreboard.getScore<PeriodGameScore>()!;
        return this.getScore(periodScore.participant1.total, periodScore.participant2.total);
    }

    private getSnookerScore(baseScoreboardViewModel: ScoreboardViewModel): string {
        const snookerModel = baseScoreboardViewModel as SnookerScoreboardViewModel;

        return this.getScore(snookerModel.stats.participant1, snookerModel.stats.participant2);
    }

    private getBaseballScore(event: MyBetsEventModel): string {
        const score = event.scoreboard.getScore<SetGameScore>()!;

        return this.getScore(score.participant1.points || 0, score.participant2.points || 0);
    }

    private getDefaultScore(event: MyBetsEventModel, baseScoreboardViewModel: ScoreboardViewModel): string {
        if (event.scoreboard.type === ScoreboardType.SetGame) {
            const scoreboardModel = baseScoreboardViewModel as SetScoreboardViewModel;
            const player1Score = sumBy(scoreboardModel.stats.sets.list, (set) => (Number(set.player1) === -1 ? 0 : Number(set.player1)));
            const player2Score = sumBy(scoreboardModel.stats.sets.list, (set) => (Number(set.player2) === -1 ? 0 : Number(set.player2)));

            return this.getScore(player1Score, player2Score);
        }

        const scoreboardModel = baseScoreboardViewModel as PairScoreboardViewModel;

        if (!scoreboardModel.stats.list.length) {
            return this.getScore(0, 0);
        }

        const stats = scoreboardModel.stats.list.find((item) => item.isVisible && item.isEnabled);

        return this.getScore(stats?.player1 || 0, stats?.player2 || 0);
    }

    private getScore(player1Score: string | number, player2Score: string | number): string {
        return `${player1Score} - ${player2Score}`;
    }
}
